import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Mail, Plus, Send, ShieldCheck, ShieldAlert, ShieldQuestion, Trash2, RefreshCw } from "lucide-react";

type Recipient = {
  id: string;
  email: string;
  enabled: boolean;
  verification_status: "unverified" | "valid" | "invalid" | "soft_fail";
  verification_reason: string | null;
  last_verified_at: string | null;
  created_at: string;
};

const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(254)
  .email({ message: "Enter a valid email address" });

export function CronAlertRecipients() {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("cron_alert_recipients")
      .select("id, email, enabled, verification_status, verification_reason, last_verified_at, created_at")
      .order("created_at", { ascending: true });
    setLoading(false);
    if (error) return toast.error(error.message);
    setRecipients((data as Recipient[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const verifyEmail = async (value: string) => {
    const { data, error } = await supabase.functions.invoke("verify-email", {
      body: { email: value },
    });
    if (error) {
      return { status: "soft_fail" as const, reason: error.message };
    }
    if (data?.valid && !data?.soft) return { status: "valid" as const, reason: null };
    if (data?.soft) return { status: "soft_fail" as const, reason: data?.reason ?? "Could not verify right now" };
    return { status: "invalid" as const, reason: (data?.reason as string) ?? "Email did not pass verification" };
  };

  const addRecipient = async () => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);

    setAdding(true);
    const value = parsed.data;
    const { data: auth } = await supabase.auth.getUser();

    const check = await verifyEmail(value);
    if (check.status === "invalid") {
      setAdding(false);
      return toast.error(check.reason ?? "Invalid email");
    }

    const { error } = await supabase.from("cron_alert_recipients").insert({
      email: value,
      enabled: true,
      verification_status: check.status,
      verification_reason: check.reason,
      last_verified_at: new Date().toISOString(),
      created_by: auth.user?.id ?? null,
    });
    setAdding(false);
    if (error) {
      if (error.code === "23505") return toast.error("That email is already on the list");
      return toast.error(error.message);
    }
    setEmail("");
    toast.success(
      check.status === "valid"
        ? "Recipient added and verified"
        : "Recipient added — verification was inconclusive",
    );
    load();
  };

  const revalidate = async (r: Recipient) => {
    setBusyId(r.id);
    const check = await verifyEmail(r.email);
    const { error } = await supabase
      .from("cron_alert_recipients")
      .update({
        verification_status: check.status,
        verification_reason: check.reason,
        last_verified_at: new Date().toISOString(),
      })
      .eq("id", r.id);
    setBusyId(null);
    if (error) return toast.error(error.message);
    toast.success(`Verification: ${check.status}`);
    load();
  };

  const toggle = async (r: Recipient, enabled: boolean) => {
    setBusyId(r.id);
    const { error } = await supabase
      .from("cron_alert_recipients")
      .update({ enabled })
      .eq("id", r.id);
    setBusyId(null);
    if (error) return toast.error(error.message);
    setRecipients((prev) => prev.map((x) => (x.id === r.id ? { ...x, enabled } : x)));
  };

  const remove = async (r: Recipient) => {
    setBusyId(r.id);
    const { error } = await supabase.from("cron_alert_recipients").delete().eq("id", r.id);
    setBusyId(null);
    if (error) return toast.error(error.message);
    setRecipients((prev) => prev.filter((x) => x.id !== r.id));
    toast.success("Recipient removed");
  };

  const sendTest = async (r: Recipient) => {
    setBusyId(r.id);
    const { data, error } = await supabase.functions.invoke("send-cron-alert-test", {
      body: { recipient_id: r.id },
    });
    setBusyId(null);
    if (error) {
      const msg = (data as { error?: string } | null)?.error ?? error.message;
      return toast.error(msg);
    }
    if ((data as { ok?: boolean })?.ok) {
      toast.success(`Test alert sent to ${r.email}`);
    } else {
      toast.error((data as { error?: string })?.error ?? "Could not send test alert");
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Mail className="size-4" /> Cron failure alert recipients
          </h3>
          <p className="text-sm text-muted-foreground">
            These addresses are notified when a cron job fails repeatedly. Emails are validated before saving.
          </p>
        </div>
      </div>

      <form
        className="flex gap-2 flex-wrap"
        onSubmit={(e) => { e.preventDefault(); addRecipient(); }}
      >
        <Input
          type="email"
          placeholder="alerts@yourcompany.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
          className="max-w-sm"
          disabled={adding}
        />
        <Button type="submit" disabled={adding || !email}>
          {adding ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
          Add & verify
        </Button>
      </form>

      <div className="space-y-2">
        {loading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : recipients.length === 0 ? (
          <div className="text-sm text-muted-foreground">No recipients yet. Add one above.</div>
        ) : (
          recipients.map((r) => <Row key={r.id} r={r} busy={busyId === r.id} onToggle={toggle} onRevalidate={revalidate} onRemove={remove} onSendTest={sendTest} />)
        )}
      </div>
    </div>
  );
}

function Row({
  r, busy, onToggle, onRevalidate, onRemove, onSendTest,
}: {
  r: Recipient;
  busy: boolean;
  onToggle: (r: Recipient, enabled: boolean) => void;
  onRevalidate: (r: Recipient) => void;
  onRemove: (r: Recipient) => void;
  onSendTest: (r: Recipient) => void;
}) {
  const badge = statusBadge(r.verification_status);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-background/50 p-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="font-mono text-sm truncate">{r.email}</div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono uppercase border ${badge.cls}`}>
            {badge.icon} {r.verification_status}
          </span>
          {r.verification_reason && (
            <span className="text-[11px] text-muted-foreground">{r.verification_reason}</span>
          )}
          {r.last_verified_at && (
            <span className="text-[11px] text-muted-foreground">
              · checked {new Date(r.last_verified_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Switch
          checked={r.enabled}
          onCheckedChange={(v) => onToggle(r, v)}
          disabled={busy}
          aria-label="Enabled"
        />
        <Button size="sm" variant="outline" onClick={() => onRevalidate(r)} disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          Verify
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onSendTest(r)}
          disabled={busy || !r.enabled}
          title={r.enabled ? "Send a test alert email" : "Enable recipient to send a test"}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          Send test
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onRemove(r)} disabled={busy} aria-label="Remove">
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function statusBadge(s: Recipient["verification_status"]) {
  switch (s) {
    case "valid":
      return { cls: "border-emerald-500/40 text-emerald-500", icon: <ShieldCheck className="size-3" /> };
    case "invalid":
      return { cls: "border-destructive/50 text-destructive", icon: <ShieldAlert className="size-3" /> };
    case "soft_fail":
      return { cls: "border-amber-500/40 text-amber-500", icon: <ShieldQuestion className="size-3" /> };
    default:
      return { cls: "border-border text-muted-foreground", icon: <ShieldQuestion className="size-3" /> };
  }
}
